import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  ICatalog,
  ICreateCatalogPayload,
  IDeleteCatalogPayload,
  ICatalogListParams,
  IUpdateCatalogPayload,
} from 'entities/Catalog/Catalog.models';
import { catalogTransport } from 'entities/Catalog/Catalog.transport';

interface IState {
  catalog: ICatalog | null;
  catalogList: ICatalog[];
  catalogCount: number;
  isCatalogLoading: boolean;
}

const initialState: IState = {
  catalog: null,
  catalogList: [],
  catalogCount: 0,
  isCatalogLoading: false,
};

export const catalogSlice = createSlice({
  name: 'catalog',
  initialState,
  reducers: {
    setCatalog(state, action: PayloadAction<ICatalog | null>) {
      state.catalog = action.payload;
    },
    setCatalogList(state, action: PayloadAction<ICatalog[]>) {
      state.catalogList = action.payload;
    },
    setCatalogCount(state, action: PayloadAction<number>) {
      state.catalogCount = action.payload;
    },
    setIsCatalogLoading(state, action: PayloadAction<boolean>) {
      state.isCatalogLoading = action.payload;
    },
    addCatalogListItem(state, action: PayloadAction<ICatalog>) {
      state.catalogList.push(action.payload);
    },
    patchCatalogListItem(state, action: PayloadAction<ICatalog>) {
      const catalog = state.catalogList.find((item) => item.id === action.payload.id);

      if (!catalog) {
        return;
      }

      Object.assign(catalog, action.payload);
    },
    removeCatalogListItem(state, action: PayloadAction<number>) {
      state.catalogList = state.catalogList.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setCatalog,
  setCatalogList,
  setCatalogCount,
  setIsCatalogLoading,
  addCatalogListItem,
  patchCatalogListItem,
  removeCatalogListItem,
} = catalogSlice.actions;

export const selectCatalog = (state: RootState) => state.catalog.catalog;
export const selectCatalogList = (state: RootState) => state.catalog.catalogList;
export const selectCatalogCount = (state: RootState) => state.catalog.catalogCount;
export const selectIsCatalogLoading = (state: RootState) => state.catalog.isCatalogLoading;

export const catalogReducer = catalogSlice.reducer;

export const getCatalogList = (params: ICatalogListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsCatalogLoading(true));

  try {
    const response = await catalogTransport.get(params);
    dispatch(setCatalogList(response.data));
    dispatch(setCatalogCount(response.count));
    return response.data;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsCatalogLoading(false));
  }
};

export const getCatalogById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsCatalogLoading(true));

  try {
    const response = await catalogTransport.getById(id);
    dispatch(setCatalog(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCatalogLoading(false));
  }
};

export const createCatalog = (payload: ICreateCatalogPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCatalogLoading(true));

  try {
    const response = await catalogTransport.create(payload);
    dispatch(setCatalog(response));
    dispatch(addCatalogListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCatalogLoading(false));
  }
};

export const updateCatalog = (payload: IUpdateCatalogPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCatalogLoading(true));

  try {
    const response = await catalogTransport.update(payload);
    dispatch(setCatalog(response));
    dispatch(patchCatalogListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCatalogLoading(false));
  }
};

export const deleteCatalog = (payload: IDeleteCatalogPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsCatalogLoading(true));

  try {
    const response = await catalogTransport.delete(payload);
    dispatch(removeCatalogListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsCatalogLoading(false));
  }
};
